import type { OnDestroy, OnInit } from '@angular/core';
import {
  ApplicationRef,
  ChangeDetectionStrategy,
  Component,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Auth, AuthEvents } from '@freelancer/auth';
import { Tracking } from '@freelancer/tracking';
import { ModalService } from '@freelancer/ui';
import { Permissions, PermissionType } from '@freelancer/ui/permissions';
import { ToastAlertService, ToastAlertType } from '@freelancer/ui/toast-alert';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subscription, firstValueFrom } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { Notifications } from './notifications.service';

/**
 * Manage the push notifications registration, i.e. register/unregister on
 * login/logout
 */
@UntilDestroy({ className: 'NotificationsComponent' })
@Component({
  selector: 'fl-notifications',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationsComponent implements OnInit, OnDestroy {
  private subscriptions = new Subscription();

  constructor(
    private appRef: ApplicationRef,
    private auth: Auth,
    private modalService: ModalService,
    private notifications: Notifications,
    private permissions: Permissions,
    private router: Router,
    private toast: ToastAlertService,
    private tracking: Tracking,
    private authEvents: AuthEvents,
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.authEvents.onAuthSessionExpired$.subscribe(() => {
        this.toast.open({
          message:
            'Your login session has expired and you have been logged out. Please log in again.',
          timeout: 10_000,
          closeable: true,
          type: ToastAlertType.WARNING,
          id: 'login-session-expired',
        });
      }),
    );

    if (this.notifications.isSupported()) {
      this.subscriptions.add(
        this.auth
          .isLoggedIn()
          .pipe(
            switchMap(async isLoggedIn => {
              // If logged-in, try registering for push notifications
              if (!isLoggedIn) {
                // This closes the permissions request modal if the user is
                // logged out. This may be necessary in the case that the user
                // reinstalls the app but has an expired token in the keychain.
                this.modalService.close();
                return;
              }

              await this.waitForAppStableBeforeRequestPermission();

              // Try to register for push notifications
              await this.notifications.enableAndRegisterNotification({
                mustOnDashboard: true,
                skipIfRequested: true,
                skipIfRegistered: false,
              });

              // register iOS VOIP push notification token on app opens
              await this.notifications.registerIosVOIPPushNotification();

              // register notification related listeners
              await this.notifications.setupNotificationListeners();
            }),
          )
          .subscribe(),
      );
    }
  }

  async waitForAppStableBeforeRequestPermission(): Promise<void> {
    const notificationsPermissions = await this.permissions.checkPermissions(
      PermissionType.PUSH_NOTIFICATIONS,
    );
    this.tracking.trackCustomEvent(
      notificationsPermissions,
      'NotificationPermissionTracker',
    );
    // This is to make sure that we don't try to request and registered permission
    // until the app has finished loading/signing in.
    if (
      this.router.url.startsWith('/login') ||
      this.router.url.startsWith('/signup') ||
      this.router.url.startsWith('/internal')
    ) {
      await firstValueFrom(
        this.router.events.pipe(
          filter(
            event =>
              event instanceof NavigationEnd &&
              event.url !== '/internal/pwa' &&
              event.url !== '/' &&
              !event.urlAfterRedirects.includes('/new-freelancer'),
          ),
          untilDestroyed(this),
        ),
      );
    }
    // also wait for the app to stabilise before prompting or registering
    await firstValueFrom(
      this.appRef.isStable.pipe(
        filter(stable => stable),
        untilDestroyed(this),
      ),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
